import { asCurrency } from '@ally/aos-utils'
import { Box, get, Table, TableColumn } from '@ally/metronome-ui'
import * as React from 'react'
import styled from 'styled-components'
import { useHostServices } from '../../../HostServices'
import StaleDataNotice from '../../../Shared/StaleDataNotice'
import { GroupedAccounts, SummarizedAccountGroup } from '../../../types'
import { getDashDash } from '../../../utils'

interface BalanceTableProps {
  accountDomain: 'INVEST' | 'BANK'
  groupedAccounts: GroupedAccounts
  selected?: string
  highlight?: {
    accountGroup: string
    highlight: boolean
  }
  onRowSelect: (category: string) => void
  hasStaleData?: boolean
}

export interface RowClickReturn<D> {
  index: number
  data: D
}

interface TableCellProps {
  cell: {
    value: React.ReactNode | string
  }
}

interface ColorBadgeProps {
  accountGroup: string
}

interface AccountLabelProps {
  accountGroup: string
  numAccounts: number
}

const StyledBox = styled(Box)`
  border-bottom: 1px solid ${get.colors('slate-2')};

  &:last-child {
    border-bottom: none;
  }
`

const StyledTableWrapper = styled.div.attrs(() => ({
  'data-testid': 'breakdown-table',
}))`
  table {
    width: 100%;
    border: none;
    margin-bottom: 3px;
    border-collapse: separate;
    border-spacing: 0 5px;
  }
  tr,
  tr:nth-child(n),
  thead,
  th:hover {
    background: transparent;
    border: none;
  }
  td {
    padding: 0;
    min-height: auto;
    height: 28px;
  }
  th {
    font-size: ${get.fontSizes('sm')};
    text-transform: capitalize;
    padding: 0;
  }
  thead {
    th {
      text-align: left;
      height: 20px;
      font-family: ${get.fonts('bold')};
    }
    th:nth-child(2) {
      text-align: right;
    }
  }
  tbody {
    tr:hover,
    tr.selected {
      background: ${get.colors('slate-1')};
      cursor: pointer;
    }
    th {
      display: flex;
      height: auto;
    }
    td {
      white-space: nowrap;
    }
  }
  .accounts-qty {
    display: inline-block;
  }
`

const StyledColorBox = styled(Box)`
  width: 5px;
  height: 28px;
  margin-right: 10.5px;
  display: inline-flex;
  overflow: hidden;
`

const labelCellFormatter = ({
  cell: { value },
}: TableCellProps): React.ReactNode | string => value

const currencyCellFormatter = ({ cell: { value } }: TableCellProps): string =>
  asCurrency(value as string)

interface KeyValuePair {
  [key: string]: string
}

interface Column {
  [key: string]: TableColumn<SummarizedAccountGroup>[]
}

interface TableConfig {
  columns: Column
  dataLabels: KeyValuePair
  labelColors: KeyValuePair
}

const balanceTableConfig = (hasStaleData: boolean): TableConfig => {
  return {
    columns: {
      INVEST: [
        {
          Header: 'Investment Accounts',
          accessor: 'accountLabel',
          isSortable: false,
          Cell: labelCellFormatter,
        },
        {
          Header: '',
          accessor: 'aggBalance',
          isSortable: false,
          Cell: hasStaleData
            ? getDashDash(
                'We can’t update your account information right now, but we’re working on it.',
              )
            : currencyCellFormatter,
        },
      ],
      BANK: [
        {
          Header: 'Bank Accounts',
          accessor: 'accountLabel',
          isSortable: false,
          Cell: labelCellFormatter,
        },
        {
          Header: '',
          accessor: 'aggBalance',
          isSortable: false,
          Cell: currencyCellFormatter,
        },
      ],
    },
    dataLabels: {
      INVW: 'Wealth Managemnet',
      INVA: 'Robo Portfolio',
      INVS: 'Self Directed',
      CHECKING: 'Interest Checking',
      SAVINGS: 'Savings',
      CD: 'CDs',
      IRA: 'IRA Plans',
    },
    labelColors: {
      INVW: 'invw',
      INVA: 'inva',
      INVS: 'inv',
      CHECKING: 'dda',
      SAVINGS: 'sda',
      CD: 'cda',
      IRA: 'ira',
    },
  }
}

interface AccountLabel {
  dataLabels: KeyValuePair
  labelColors: KeyValuePair
}

const accountLabelConfig = (
  isProductNameChangeEnabled = false,
): AccountLabel => {
  return {
    dataLabels: {
      INVW: 'Wealth Management',
      INVA: 'Robo Portfolio',
      INVS: 'Self Directed',
      CHECKING: isProductNameChangeEnabled ? 'Checking' : 'Interest Checking',
      SAVINGS: 'Savings',
      CD: 'CDs',
      IRA: isProductNameChangeEnabled ? 'IRAs' : 'IRA Plans',
    },
    labelColors: {
      INVW: 'invw',
      INVA: 'inva',
      INVS: 'inv',
      CHECKING: 'dda',
      SAVINGS: 'sda',
      CD: 'cda',
      IRA: 'ira',
    },
  }
}

const ColorBadge: React.FC<ColorBadgeProps> = ({
  accountGroup,
}): React.ReactElement => {
  const colors: { [key: string]: string } = accountLabelConfig().labelColors
  return <StyledColorBox bg={colors[accountGroup]}>&nbsp;</StyledColorBox>
}

const AccountLabel: React.FC<AccountLabelProps> = ({
  accountGroup,
  numAccounts,
}): React.ReactElement => {
  const { featureFlags } = useHostServices()
  const isProductNameChangeEnabled = featureFlags.variation(
    'FF_product-name-change',
    false,
  )

  const labels: { [key: string]: string } = accountLabelConfig(
    isProductNameChangeEnabled,
  ).dataLabels
  const accountsQty = numAccounts > 1 ? `(${numAccounts} accounts)` : ``
  return (
    <Box display="flex" alignItems="center">
      <ColorBadge accountGroup={accountGroup} />
      <Box lineHeight="initial">
        {labels[accountGroup]}&nbsp;
        <span className="accounts-qty">{accountsQty}</span>
      </Box>
    </Box>
  )
}

const formatData = (
  accounts: SummarizedAccountGroup[],
): SummarizedAccountGroup[] => {
  const formattedData = accounts.map(account => {
    account.accountLabel = (
      <AccountLabel
        accountGroup={account.accountGroup}
        numAccounts={account.numberOfAccounts}
      />
    )
    return account
  })
  return Object.values(formattedData)
}

const BalanceTable: React.FC<BalanceTableProps> = ({
  groupedAccounts,
  accountDomain,
  selected,
  highlight = {
    accountGroup: '',
    highlight: false,
  },
  onRowSelect,
  hasStaleData = false,
}): React.ReactElement => {
  const tableClass = `${accountDomain}-balance-table`

  const getTableRow = React.useCallback(
    (accountGroup: string): HTMLElement | null => {
      const rowIndex = Object.values(groupedAccounts).findIndex(
        account => account.accountGroup === accountGroup,
      )

      return rowIndex >= 0
        ? document.querySelector(
            `table.${tableClass} tbody tr:nth-of-type(${rowIndex + 1})`,
          )
        : null
    },
    [groupedAccounts, tableClass],
  )

  React.useEffect(() => {
    const tableRow = getTableRow(highlight.accountGroup)
    if (tableRow) {
      tableRow.style.background = highlight.highlight
        ? 'rgba(229, 229, 229)'
        : ''
    }
  }, [highlight, getTableRow])

  const selectedRow = (): number | null => {
    const row = Object.values(groupedAccounts).findIndex(
      account => account.accountGroup === selected,
    )
    return row >= 0 ? row : null
  }

  const onRowClick = (row: RowClickReturn<SummarizedAccountGroup>): void => {
    if (onRowSelect) {
      onRowSelect(row.data.accountGroup)
    }
  }

  return (
    <StyledBox>
      <StyledTableWrapper>
        {hasStaleData && <StaleDataNotice />}
        <Table
          captionText=""
          data={formatData(Object.values(groupedAccounts))}
          data-testid="test"
          columns={balanceTableConfig(hasStaleData).columns[accountDomain]}
          className={tableClass}
          withRowHeadings
          suppressMdDownLayout
          selectRow={selectedRow()}
          rowClickHandler={onRowClick}
        />
      </StyledTableWrapper>
    </StyledBox>
  )
}

export default BalanceTable
